import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import useWindowSize from '@hooks/useWindowSize';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import GridContainer from '@cont/CitySpecificOfflineCentres/CommonComponents';
import Button from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography';
import pushRoute from '@utils/pushRoute';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';
import { segment } from '@actions/analytics';
import { OFFLINE_VISIT_THE_CENTRE } from '@cont/CitySpecificOfflineCentres/constants';
import { useRouter } from 'next/router';
import useCitySpecificiOfflineCentreStore from '@stores/city-specific-offline-centres';
import { useEffect, useState } from 'react';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';

const Container = styled.div`
  grid-column: span 24;
  width: 100%;
  position: relative;
  margin-top: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_16B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const StyledHeading = styled(Typography)`
  font-size: ${Fonts.H2};
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_4B};
  line-height: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    font-weight: ${Fonts.HEAVY};
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;
const ColoredHeading = styled.span`
  color: var(--color-i-violet-15);
`;

const StyledSubHeading = styled(Typography)`
  color: var(--color-text-secondary);
  width: 50%;
  margin-bottom: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H5};
    font-weight: ${Fonts.LIGHT};
    line-height: ${Spacings.SPACING_6B};
    width: 100%;
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const VisitCentreCTA = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  ${mediaQuery.uptoMobile} {
    position: relative;
    padding-top: ${Spacings.SPACING_6B};
    padding-bottom: ${Spacings.SPACING_3B};
    width: 100%;
    margin
  }
`;

const StyButton = styled(Button)`
  width: 224px;
  height: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const LearnerVisitedData = styled.div`
  width: 100%;
  justify-content: center;
  margin-bottom: ${Spacings.SPACING_6B};
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_4B};
`;

const Stats = styled(Typography)`
  margin-left: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const WhyUnacademyThumbnailWrapper = styled.div`
  height: 988px;
  width: 1136px;
  position: relative;
  ${mediaQuery.uptoMobile} {
    max-height: 88vh;
    width: 100%;
  }
`;

const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

const loadingStateAnimation = (
  <AnimationContainer
    Fallback={
      <AnimationFallBackImage imageURL="offline-centres/membersAttempting.svg" />
    }
    src={getAnimationPath('offlineCentres/JoiningMembers.json')}
  />
);

const WhyUnacademy = ({ data }) => {
  const {
    heading,
    descriptionWeb,
    description,
    ctaText,
    ctaDeepLink,
    learnersVisitedText,
    thumbnailWeb,
    thumbnail
  } = data || {};
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { isMobile } = useWindowSize();

  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { goalInfo: goalDetails } = useGoalInfo(routeGoalUID);
  const goalName = goalDetails?.name || '';
  const {
    centresListData,
    isBookAVisitEnabledFromCMS,
    setBookAVisitClicked,
    setBookAVisitFlow,
    isBookAVisitClicked
  } = useCitySpecificiOfflineCentreStore();
  const [centreData, setCentreData] = useState({});

  useEffect(() => {
    setCentreData(
      centresListData?.find((centre) => {
        return centre.uid === centreUID;
      })
    );
  }, [centresListData, centreUID]);

  useEffect(() => {
    if (isLoggedIn) {
      if (isBookAVisitClicked) {
        setBookAVisitClicked(false);
        setBookAVisitFlow(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const { centre_name, city, state } = centreData || {};

  const handleClick = () => {
    segment.track(OFFLINE_VISIT_THE_CENTRE, {
      centre_name,
      city,
      state,
      goal_name: goalName,
      goal_uid: routeGoalUID,
      block_position: 'Middle'
    });

    if (!isLoggedIn) {
      openLoginDrawer();
      if (isBookAVisitEnabledFromCMS) setBookAVisitClicked(true);
    } else if (isBookAVisitEnabledFromCMS) {
      setBookAVisitFlow(true);
    } else pushRoute(ctaDeepLink, null, null, null, null, false);
  };

  return (
    <GridContainer>
      <Container>
        {heading ? (
          <StyledHeading variant="h4">{heading}</StyledHeading>
        ) : (
          <StyledHeading variant="h4">
            Why <ColoredHeading>Unacademy</ColoredHeading>?
          </StyledHeading>
        )}
        <StyledSubHeading variant="p1">
          {isMobile ? description : descriptionWeb}
        </StyledSubHeading>
        <WhyUnacademyThumbnailWrapper>
          <Image
            src={isMobile ? thumbnail : thumbnailWeb}
            // src="https://unacademy-prod.s3.ap-southeast-1.amazonaws.com/web-cms/offline_package_mobile_4c5b66f48f.png"
            layout="fill"
            alt="whyUnacademy"
          />
        </WhyUnacademyThumbnailWrapper>
        <VisitCentreCTA>
          <StyButton size="large" label={ctaText} onClick={handleClick} />
        </VisitCentreCTA>

        {isMobile && ( // get this to work
          <LearnerVisitedData>
            {loadingStateAnimation}
            <Stats variant="p2">{learnersVisitedText}</Stats>
          </LearnerVisitedData>
        )}
      </Container>
    </GridContainer>
  );
};

export default WhyUnacademy;
